import { useRef, useState, useCallback, useEffect } from "react";
import Editor, { type OnMount, type OnChange } from "@monaco-editor/react";
import type {
  FieldDefinition,
  FieldType,
  RichTextValue,
} from "@/types/template";
import { FIELD_TYPES } from "@/types/template";
import { insertFieldIntoCode } from "@/lib/fieldCodeSync";

interface CodeEditorProps {
  value: string;
  onChange: (value: string) => void;
  language?: string;
  readOnly?: boolean;
  minHeight?: string;
  /** Controlled fullscreen state — parent drives via Ctrl+` handler. */
  isFullscreen?: boolean;
  /** Called when the editor wants to toggle fullscreen (button click, Escape). */
  onToggleFullscreen?: () => void;
  /** If provided, shows an "Insert Field" button that adds a prop to the
   *  code AND to the field schema in one action. */
  onInsertField?: (field: FieldDefinition, code: string) => void;
}

export function CodeEditor({
  value,
  onChange,
  language = "typescript",
  readOnly = false,
  minHeight = "300px",
  isFullscreen = false,
  onToggleFullscreen,
  onInsertField,
}: CodeEditorProps) {
  const editorRef = useRef<Parameters<OnMount>[0] | null>(null);
  const viewStateRef = useRef<ReturnType<Parameters<OnMount>[0]["saveViewState"]> | null>(null);
  const [showFieldForm, setShowFieldForm] = useState(false);
  const [newFieldKey, setNewFieldKey] = useState("");
  const [newFieldLabel, setNewFieldLabel] = useState("");
  // text-rich, not plain text: the backend rejects new templates with
  // text/textarea fields, so the insert tool never offers them.
  const [newFieldType, setNewFieldType] = useState<FieldType>("text-rich");

  const handleMount: OnMount = useCallback((editor, monaco) => {
    editorRef.current = editor;

    // Restore view state if we have one from the previous mount (inline ↔ fullscreen toggle)
    if (viewStateRef.current) {
      editor.restoreViewState(viewStateRef.current);
    }

    // Configure TypeScript/TSX defaults
    monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
      target: monaco.languages.typescript.ScriptTarget.ESNext,
      module: monaco.languages.typescript.ModuleKind.ESNext,
      jsx: monaco.languages.typescript.JsxEmit.ReactJSX,
      allowJs: true,
      strict: false,
      noEmit: true,
      esModuleInterop: true,
      moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
    });

    // Add Remotion type stubs so the editor doesn't show import errors
    monaco.languages.typescript.typescriptDefaults.addExtraLib(
      `
      declare module 'remotion' {
        export function useCurrentFrame(): number;
        export function useVideoConfig(): { fps: number; width: number; height: number; durationInFrames: number };
        export function interpolate(input: number, inputRange: number[], outputRange: number[], options?: { extrapolateLeft?: string; extrapolateRight?: string }): number;
        export function spring(args: { frame: number; fps: number; config?: { damping?: number; stiffness?: number; mass?: number } }): number;
        export const AbsoluteFill: React.FC<{ style?: React.CSSProperties; children?: React.ReactNode }>;
        export const Sequence: React.FC<{ from?: number; durationInFrames?: number; children?: React.ReactNode }>;
        export const Img: React.FC<{ src: string; style?: React.CSSProperties }>;
        export const Composition: React.FC<any>;
      }
      declare module 'react' {
        export default React;
        export const useState: typeof React.useState;
        export const useEffect: typeof React.useEffect;
        export const useMemo: typeof React.useMemo;
        export const useCallback: typeof React.useCallback;
        export const useRef: typeof React.useRef;
        export type FC<P = {}> = React.FC<P>;
        export type CSSProperties = React.CSSProperties;
        export type ReactNode = React.ReactNode;
      }
      `,
      "remotion.d.ts"
    );
  }, []);

  const handleChange: OnChange = useCallback(
    (val) => {
      if (val !== undefined) onChange(val);
    },
    [onChange]
  );

  // Save view state before unmounting so the next mount can restore it
  const handleToggle = useCallback(() => {
    if (editorRef.current) {
      viewStateRef.current = editorRef.current.saveViewState();
    }
    onToggleFullscreen?.();
  }, [onToggleFullscreen]);

  // Escape exits fullscreen
  useEffect(() => {
    if (!isFullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleToggle();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isFullscreen, handleToggle]);

  // Lock body scroll while fullscreen is open.
  useEffect(() => {
    if (!isFullscreen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isFullscreen]);

  const editorOptions = {
    readOnly,
    fontSize: 13,
    fontFamily: "'JetBrains Mono', monospace",
    minimap: { enabled: isFullscreen },
    lineNumbers: "on" as const,
    scrollBeyondLastLine: false,
    wordWrap: "on" as const,
    tabSize: 2,
    automaticLayout: true,
    bracketPairColorization: { enabled: true },
    padding: { top: 12, bottom: 12 },
    renderWhitespace: "selection" as const,
    overviewRulerLanes: 0,
    hideCursorInOverviewRuler: true,
    scrollbar: {
      verticalScrollbarSize: 8,
      horizontalScrollbarSize: 8,
    },
  };

  const ExpandIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
    </svg>
  );
  const CollapseIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 14h6v6M20 10h-6V4M14 10l7-7M3 21l7-7" />
    </svg>
  );

  const handleInsertField = useCallback(() => {
    if (!newFieldKey.trim() || !onInsertField) return;
    const key = newFieldKey.trim().replace(/[^a-zA-Z0-9_]/g, "");
    if (!key) return;

    const DEFAULT_FOR_TYPE: Record<
      FieldType,
      string | number | boolean | RichTextValue
    > = {
      text: "", textarea: "", "text-rich": { text: "", font: "Rubik", fontSize: 64 },
      number: 0, slider: 50, knob: 50,
      color: "#ffffff", "color-alpha": "#ffffffff", image: "",
      select: "", boolean: false,
  layer: true,
    };

    const field: FieldDefinition = {
      key,
      label: newFieldLabel.trim() || key,
      type: newFieldType,
      default: DEFAULT_FOR_TYPE[newFieldType],
      required: false,
    };

    // Patch the code to include the new prop
    const updatedCode = insertFieldIntoCode(value, key, newFieldType);
    onInsertField(field, updatedCode);

    // Reset form
    setNewFieldKey("");
    setNewFieldLabel("");
    setNewFieldType("text-rich");
    setShowFieldForm(false);
  }, [newFieldKey, newFieldLabel, newFieldType, value, onInsertField]);

  const inputClasses =
    "rounded-lg border border-line bg-surface px-2 py-1 text-xs text-ink focus:border-line-strong focus:outline-none";

  const headerBar = (
    <div className="border-b border-line bg-surface-2">
      <div className="flex items-center justify-between px-3 py-1.5">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-ink-3">
            {language === "tsx" ? "TSX" : language.toUpperCase()}
            {isFullscreen && <span className="ml-2 text-ink-3">— Esc to exit</span>}
          </span>
          {onInsertField && (
            <button
              type="button"
              onClick={() => setShowFieldForm((v) => !v)}
              className={`rounded-md px-2 py-0.5 text-xs font-medium transition-colors ${
                showFieldForm
                  ? "bg-accent-soft text-accent"
                  : "text-ink-2 hover:text-ink"
              }`}
            >
              + Insert Field
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={handleToggle}
          title={isFullscreen ? "Exit fullscreen (Esc or Ctrl+`)" : "Fullscreen (Ctrl+`)"}
          aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-2 py-1 text-xs font-medium text-ink-2 transition-colors hover:bg-surface-3 hover:text-ink"
        >
          {isFullscreen ? <CollapseIcon /> : <ExpandIcon />}
          {isFullscreen ? "Exit" : "Fullscreen"}
        </button>
      </div>
      {showFieldForm && onInsertField && (
        <div className="flex items-center gap-2 border-t border-line px-3 py-2">
          <input
            type="text"
            value={newFieldKey}
            onChange={(e) => setNewFieldKey(e.target.value.replace(/[^a-zA-Z0-9_]/g, ""))}
            placeholder="propName"
            className={`w-28 ${inputClasses}`}
            onKeyDown={(e) => { if (e.key === "Enter") handleInsertField(); }}
          />
          <input
            type="text"
            value={newFieldLabel}
            onChange={(e) => setNewFieldLabel(e.target.value)}
            placeholder="Display Label"
            className={`w-32 ${inputClasses}`}
            onKeyDown={(e) => { if (e.key === "Enter") handleInsertField(); }}
          />
          <select
            value={newFieldType}
            onChange={(e) => setNewFieldType(e.target.value as FieldType)}
            className={`w-28 ${inputClasses}`}
          >
            {FIELD_TYPES.filter(
              (ft) => ft.value !== "text" && ft.value !== "textarea",
            ).map((ft) => (
              <option key={ft.value} value={ft.value}>{ft.label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleInsertField}
            disabled={!newFieldKey.trim()}
            className="rounded-lg bg-accent px-3 py-1 text-xs font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:opacity-40"
          >
            Add
          </button>
          <button
            type="button"
            onClick={() => setShowFieldForm(false)}
            className="text-xs text-ink-2 hover:text-ink"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );

  // Shared editor props — same `path` ensures Monaco reuses one model
  // across inline ↔ fullscreen, preserving undo/redo history.
  const editorProps = {
    language: language === "tsx" ? "typescript" : language,
    value,
    onChange: handleChange,
    onMount: handleMount,
    theme: "vs-dark" as const,
    options: editorOptions,
    path: "motionforge-editor.tsx",
  };

  if (isFullscreen) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-surface">
        {headerBar}
        <div className="flex-1">
          <Editor height="100%" {...editorProps} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-line">
      {headerBar}
      {/* Fill whatever height the parent grants (workspace Code tab gives
          the whole pane); minHeight is the floor for height-less contexts
          like TemplateDetail's inline editor. */}
      <div className="min-h-0 flex-1" style={{ minHeight }}>
        <Editor height="100%" {...editorProps} />
      </div>
    </div>
  );
}
